/**
 * Landmarks Renderer
 * Renders MediaPipe face and hand landmarks on a canvas overlay
 */

export class LandmarksRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    // Rendering options - Matrix style
    this.options = {
      face: {
        enabled: true,
        pointColor: 'rgba(0, 255, 0, 0.95)', // Bright Matrix green
        pointRadius: 3,
        connectionColor: 'rgba(0, 255, 0, 0.7)', // Matrix green connections
        connectionWidth: 2,
        showConnections: true, // Enable connections for Matrix style
      },
      hands: {
        enabled: true,
        leftColor: 'rgba(0, 255, 200, 0.95)', // Cyan-green Matrix for left hand
        rightColor: 'rgba(0, 255, 150, 0.95)', // Bright green-cyan Matrix for right hand
        pointRadius: 3,
        connectionColor: 'rgba(0, 255, 180, 0.75)', // Bright cyan-green connections for hands
        connectionWidth: 2.5,
        showConnections: true,
      },
      gaze: {
        enabled: true,
        arrowColor: 'rgba(255, 255, 0, 0.9)', // Yellow for gaze
        arrowLength: 50,
        arrowWidth: 3,
      },
    };

    // MediaPipe hand connections (for drawing skeleton)
    this.handConnections = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 4], // Thumb
      [0, 5],
      [5, 6],
      [6, 7],
      [7, 8], // Index
      [0, 9],
      [9, 10],
      [10, 11],
      [11, 12], // Middle
      [0, 13],
      [13, 14],
      [14, 15],
      [15, 16], // Ring
      [0, 17],
      [17, 18],
      [18, 19],
      [19, 20], // Pinky
      [5, 9],
      [9, 13],
      [13, 17], // Palm connections
    ];

    // Key face landmarks for simplified visualization
    this.faceLandmarksSubset = {
      // Outline
      outline: [
        10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377,
        152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109,
      ],
      // Eyes
      leftEye: [33, 246, 161, 160, 159, 158, 157, 173, 133, 155, 154, 153, 145, 144, 163, 7],
      rightEye: [362, 398, 384, 385, 386, 387, 388, 466, 263, 249, 390, 373, 374, 380, 381, 382],
      // Eyebrows
      leftEyebrow: [70, 63, 105, 66, 107, 55, 65, 52, 53, 46],
      rightEyebrow: [336, 296, 334, 293, 300, 276, 283, 282, 295, 285],
      // Nose - expanded for better visibility when nose alert is detected
      nose: [
        1, 2, 4, 5, 19, 20, 44, 45, 49, 51, 64, 98, 102, 115, 131, 134, 174, 196, 197, 
        209, 217, 220, 236, 240, 275, 274, 279, 281, 294, 327, 358, 360, 399, 419, 429, 437
      ],
      // Lips
      lips: [
        61, 185, 40, 39, 37, 0, 267, 269, 270, 409, 291, 375, 321, 405, 314, 17, 84, 181, 91, 146,
      ],
    };

    // Face connections for Matrix-style rendering (based on MediaPipe Face Mesh topology)
    this.faceConnections = [];
    this.buildFaceConnections();
  }

  /**
   * Build face connections based on MediaPipe Face Mesh topology
   * Creates connections for main facial features using MediaPipe indices
   */
  buildFaceConnections() {
    // Main MediaPipe Face Mesh connections for key features
    // These use the actual MediaPipe landmark indices (0-467)

    // Face outline (contour) - REMOVED: user requested to remove outer edges
    // const outlineIndices = this.faceLandmarksSubset.outline || [];
    // for (let i = 0; i < outlineIndices.length - 1; i++) {
    //   this.faceConnections.push([outlineIndices[i], outlineIndices[i + 1]]);
    // }
    // // Close the loop
    // if (outlineIndices.length > 0) {
    //   this.faceConnections.push([outlineIndices[outlineIndices.length - 1], outlineIndices[0]]);
    // }

    // Left eye - sequential connections
    const leftEyeIndices = this.faceLandmarksSubset.leftEye || [];
    for (let i = 0; i < leftEyeIndices.length - 1; i++) {
      this.faceConnections.push([leftEyeIndices[i], leftEyeIndices[i + 1]]);
    }
    // Close the loop
    if (leftEyeIndices.length > 0) {
      this.faceConnections.push([leftEyeIndices[leftEyeIndices.length - 1], leftEyeIndices[0]]);
    }

    // Right eye - sequential connections
    const rightEyeIndices = this.faceLandmarksSubset.rightEye || [];
    for (let i = 0; i < rightEyeIndices.length - 1; i++) {
      this.faceConnections.push([rightEyeIndices[i], rightEyeIndices[i + 1]]);
    }
    // Close the loop
    if (rightEyeIndices.length > 0) {
      this.faceConnections.push([rightEyeIndices[rightEyeIndices.length - 1], rightEyeIndices[0]]);
    }

    // Left eyebrow - sequential connections
    const leftEyebrowIndices = this.faceLandmarksSubset.leftEyebrow || [];
    for (let i = 0; i < leftEyebrowIndices.length - 1; i++) {
      this.faceConnections.push([leftEyebrowIndices[i], leftEyebrowIndices[i + 1]]);
    }

    // Right eyebrow - sequential connections
    const rightEyebrowIndices = this.faceLandmarksSubset.rightEyebrow || [];
    for (let i = 0; i < rightEyebrowIndices.length - 1; i++) {
      this.faceConnections.push([rightEyebrowIndices[i], rightEyebrowIndices[i + 1]]);
    }

    // Nose - sequential connections
    const noseIndices = this.faceLandmarksSubset.nose || [];
    for (let i = 0; i < noseIndices.length - 1; i++) {
      this.faceConnections.push([noseIndices[i], noseIndices[i + 1]]);
    }

    // Lips - sequential connections (close the loop)
    const lipsIndices = this.faceLandmarksSubset.lips || [];
    for (let i = 0; i < lipsIndices.length - 1; i++) {
      this.faceConnections.push([lipsIndices[i], lipsIndices[i + 1]]);
    }
    // Close the loop for lips
    if (lipsIndices.length > 0) {
      this.faceConnections.push([lipsIndices[lipsIndices.length - 1], lipsIndices[0]]);
    }
  }

  /**
   * Clear the canvas
   */
  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Render landmarks data for current frame
   * @param {Object} landmarksData - Landmarks data for current frame
   * @param {Object} toggleOptions - Toggle options for face/hands/gaze
   * @param {Object} landmarkColors - Color configuration: { faceRegions: {...}, fullHead: '...', hands: '...' }
   * @param {Object|null} alertOverlay - Optional overlay badge: { label: string, tone: 'error'|'warning'|'success' }
   */
  render(
    landmarksData,
    toggleOptions = null,
    landmarkColors = null,
    alertOverlay = null
  ) {
    if (!landmarksData) return;

    // Update options if provided
    if (toggleOptions) {
      this.options.face.enabled = toggleOptions.face !== false;
      this.options.hands.enabled = toggleOptions.hands !== false;
      this.options.gaze.enabled = toggleOptions.gaze !== false;
    }

    // Clear canvas
    this.clear();

    const width = this.canvas.width;
    const height = this.canvas.height;

    // Render face landmarks
    if (
      this.options.face.enabled &&
      landmarksData.face_landmarks &&
      landmarksData.face_landmarks.length > 0
    ) {
      this.renderFaceLandmarks(landmarksData.face_landmarks, width, height, landmarkColors);
    }

    // Render hand landmarks
    if (this.options.hands.enabled) {
      const handsColor = landmarkColors?.hands || 'success';
      if (landmarksData.left_hand_landmarks && landmarksData.left_hand_landmarks.length > 0) {
        this.renderHandLandmarks(landmarksData.left_hand_landmarks, width, height, 'left', handsColor);
      }

      if (landmarksData.right_hand_landmarks && landmarksData.right_hand_landmarks.length > 0) {
        this.renderHandLandmarks(landmarksData.right_hand_landmarks, width, height, 'right', handsColor);
      }
    }

    // Render gaze direction - check multiple possible data structures
    if (this.options.gaze.enabled) {
      const gazeData =
        landmarksData.gaze_data ||
        (landmarksData['Gaze Z'] !== undefined && landmarksData['Gaze Y'] !== undefined ? {
          'Gaze Z': landmarksData['Gaze Z'],
          'Gaze Y': landmarksData['Gaze Y']
        } : null) ||
        (landmarksData.gaze_z !== undefined && landmarksData.gaze_y !== undefined ? {
          'Gaze Z': landmarksData.gaze_z,
          'Gaze Y': landmarksData.gaze_y
        } : null);

      if (gazeData) {
        this.renderGazeDirection(gazeData, width, height);
      }
    }

    this.renderEntityBoxes(landmarksData, width, height, landmarkColors, alertOverlay);
  }

  getTonePalette(tone) {
    const palette = {
      error: {
        stroke: 'rgba(198, 40, 40, 0.98)',
        fill: 'rgba(198, 40, 40, 0.16)',
        badgeBg: 'rgba(183, 28, 28, 0.95)',
        badgeBorder: 'rgba(255, 205, 210, 0.78)',
      },
      warning: {
        stroke: 'rgba(245, 124, 0, 0.98)',
        fill: 'rgba(245, 124, 0, 0.16)',
        badgeBg: 'rgba(230, 81, 0, 0.95)',
        badgeBorder: 'rgba(255, 224, 178, 0.75)',
      },
      success: {
        stroke: 'rgba(76, 175, 80, 0.95)',
        fill: 'rgba(76, 175, 80, 0.12)',
        badgeBg: 'rgba(27, 94, 32, 0.9)',
        badgeBorder: 'rgba(200, 230, 201, 0.65)',
      },
    };
    return palette[tone] || palette.success;
  }

  resolveFaceTone(landmarkColors) {
    if (!landmarkColors) return 'success';
    if (landmarkColors.fullHead === 'error' || landmarkColors.fullHead === 'warning') {
      return landmarkColors.fullHead;
    }
    const regions = landmarkColors.faceRegions || {};
    const values = [regions.brows, regions.lips, regions.nose, regions.eyes, regions.mouth];
    if (values.includes('error')) return 'error';
    if (values.includes('warning')) return 'warning';
    return 'success';
  }

  calculateBoundingBox(landmarks, width, height, paddingPx = 12) {
    if (!Array.isArray(landmarks) || landmarks.length === 0) return null;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    landmarks.forEach((landmark) => {
      const x = landmark.x * width;
      const y = landmark.y * height;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });

    if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
      return null;
    }

    const x = Math.max(0, minX - paddingPx);
    const y = Math.max(0, minY - paddingPx);
    const w = Math.min(width - x, maxX - minX + paddingPx * 2);
    const h = Math.min(height - y, maxY - minY + paddingPx * 2);
    if (w <= 2 || h <= 2) return null;
    return { x, y, w, h };
  }

  drawBoundingBox(box, tone, lineWidth = 2, label = '') {
    if (!box) return;
    const ctx = this.ctx;
    const palette = this.getTonePalette(tone);
    ctx.save();
    ctx.fillStyle = palette.fill;
    ctx.strokeStyle = palette.stroke;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.rect(box.x, box.y, box.w, box.h);
    ctx.fill();
    ctx.stroke();

    const cleanLabel = String(label || '').trim();
    if (cleanLabel) {
      const startFontSize = 11;
      const minFontSize = 8;
      const fontFamily = 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      const paddingX = 6;
      const tagHeight = 18;
      const textY = Math.max(2, box.y - tagHeight - 4);
      let fontSize = startFontSize;
      ctx.font = `600 ${fontSize}px ${fontFamily}`;
      let textWidth = ctx.measureText(cleanLabel).width;
      const tagWidth = Math.max(24, Math.min(box.w, textWidth + paddingX * 2));
      const tagX = Math.max(0, Math.min(this.canvas.width - tagWidth, box.x));
      const maxTextWidth = Math.max(0, tagWidth - paddingX * 2);

      while (fontSize > minFontSize && textWidth > maxTextWidth) {
        fontSize -= 1;
        ctx.font = `600 ${fontSize}px ${fontFamily}`;
        textWidth = ctx.measureText(cleanLabel).width;
      }

      let fittedText = cleanLabel;
      if (textWidth > maxTextWidth) {
        const ellipsis = '...';
        while (
          fittedText.length > 1 &&
          ctx.measureText(`${fittedText}${ellipsis}`).width > maxTextWidth
        ) {
          fittedText = fittedText.slice(0, -1);
        }
        fittedText = `${fittedText}${ellipsis}`;
      }

      ctx.fillStyle = palette.badgeBg;
      ctx.strokeStyle = palette.badgeBorder;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.rect(tagX, textY, tagWidth, tagHeight);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = 'rgba(255, 255, 255, 0.98)';
      ctx.textBaseline = 'middle';
      ctx.fillText(fittedText, tagX + paddingX, textY + tagHeight / 2);
    }
    ctx.restore();
  }

  renderEntityBoxes(
    landmarksData,
    width,
    height,
    landmarkColors,
    alertOverlay = null
  ) {
    if (alertOverlay?.showBoundingBoxes === false) {
      return;
    }
    const boxColors = alertOverlay?.boxColors || landmarkColors;
    const faceTone = this.resolveFaceTone(boxColors);
    const handsTone = (
      boxColors?.hands === 'error' || boxColors?.hands === 'warning'
        ? boxColors.hands
        : 'success'
    );
    const label = String(alertOverlay?.label || '').trim();

    if (
      this.options.face.enabled &&
      Array.isArray(landmarksData?.face_landmarks)
    ) {
      const faceBox = this.calculateBoundingBox(landmarksData.face_landmarks, width, height, 14);
      this.drawBoundingBox(
        faceBox,
        faceTone,
        2.2,
        label
      );
    }

    if (this.options.hands.enabled) {
      if (
        Array.isArray(landmarksData?.left_hand_landmarks) &&
        landmarksData.left_hand_landmarks.length > 0
      ) {
        const leftHandBox = this.calculateBoundingBox(landmarksData.left_hand_landmarks, width, height, 10);
        this.drawBoundingBox(leftHandBox, handsTone, 2, label);
      }
      if (
        Array.isArray(landmarksData?.right_hand_landmarks) &&
        landmarksData.right_hand_landmarks.length > 0
      ) {
        const rightHandBox = this.calculateBoundingBox(landmarksData.right_hand_landmarks, width, height, 10);
        this.drawBoundingBox(
          rightHandBox,
          handsTone,
          2,
          label
        );
      }
    }
  }

  /**
   * Calculate bounding box area for landmarks
   */
  calculateBoundingBoxArea(landmarks, width, height) {
    if (!landmarks || landmarks.length === 0) return 0;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    landmarks.forEach((landmark) => {
      const x = landmark.x * width;
      const y = landmark.y * height;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    });

    const area = (maxX - minX) * (maxY - minY);
    return area;
  }

  /**
   * Calculate adaptive line width and point radius based on bounding box area
   * Smaller area = thinner lines, larger area = thicker lines
   * But within reasonable bounds to avoid overlapping
   */
  calculateAdaptiveSize(area, canvasArea, minRadius = 1, maxRadius = 2.5, minWidth = 0.8, maxWidth = 2) {
    // Normalize area relative to canvas (0-1)
    const normalizedArea = Math.min(1, Math.max(0, area / canvasArea));

    // Use direct relationship: smaller area = smaller size, larger area = larger size
    // Apply smooth curve (square root for smoother transition)
    const curve = Math.sqrt(normalizedArea);

    // Calculate radius and width with bounds
    // Smaller area -> smaller normalizedArea -> smaller curve -> smaller radius/width
    // Larger area -> larger normalizedArea -> larger curve -> larger radius/width
    const radius = minRadius + (maxRadius - minRadius) * curve;
    const width = minWidth + (maxWidth - minWidth) * curve;

    return { radius, width };
  }

  /**
   * Get color for a specific region
   * @param {string} colorName - 'error', 'warning', or 'success'
   * @returns {string} - RGBA color string
   */
  getColorForRegion(colorName) {
    const colors = {
      error: 'rgba(255, 152, 0, 0.98)', // Gesture alert orange
      warning: 'rgba(255, 214, 10, 0.98)', // Gesture warning yellow
      success: 'rgba(76, 175, 80, 0.95)', // Success green
      // Legacy support for old color names
      red: 'rgba(255, 152, 0, 0.98)',
      orange: 'rgba(255, 152, 0, 0.95)',
      green: 'rgba(76, 175, 80, 0.95)',
      normal: 'rgba(76, 175, 80, 0.95)', // normal = success
    };
    return colors[colorName] || colors.success;
  }

  /**
   * Get connection color for a specific region
   * @param {string} colorName - 'error', 'warning', or 'success'
   * @returns {string} - RGBA color string
   */
  getConnectionColorForRegion(colorName) {
    const colors = {
      error: 'rgba(255, 152, 0, 0.78)',
      warning: 'rgba(255, 214, 10, 0.78)',
      success: 'rgba(76, 175, 80, 0.7)',
      // Legacy support for old color names
      red: 'rgba(255, 152, 0, 0.78)',
      orange: 'rgba(255, 152, 0, 0.7)',
      green: 'rgba(76, 175, 80, 0.7)',
      normal: 'rgba(76, 175, 80, 0.7)', // normal = success
    };
    return colors[colorName] || colors.success;
  }

  /**
   * Render face landmarks with Matrix-style connections, colored by region
   * @param {Array} landmarks - Face landmarks array
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   * @param {Object} landmarkColors - Color configuration: { faceRegions: {...}, fullHead: '...' }
   */
  renderFaceLandmarks(landmarks, width, height, landmarkColors = null) {
    const ctx = this.ctx;

    // Calculate adaptive sizes based on face bounding box
    const faceArea = this.calculateBoundingBoxArea(landmarks, width, height);
    const canvasArea = width * height; // Calculate canvas area
    const adaptiveSizes = this.calculateAdaptiveSize(faceArea, canvasArea, 1, 2.5, 1, 2);

    // Determine overall face color
    let overallFaceColor = 'success';
    if (landmarkColors) {
      if (landmarkColors.fullHead && landmarkColors.fullHead !== 'success') {
        overallFaceColor = landmarkColors.fullHead;
      } else if (landmarkColors.faceRegions) {
        // Check if any region has an alert
        const regions = landmarkColors.faceRegions;
        if (regions.brows === 'error' || regions.lips === 'error' || regions.nose === 'error' || 
            regions.eyes === 'error' || regions.mouth === 'error') {
          overallFaceColor = 'error';
        } else if (regions.brows === 'warning' || regions.lips === 'warning' || regions.nose === 'warning' || 
                   regions.eyes === 'warning' || regions.mouth === 'warning') {
          overallFaceColor = 'warning';
        }
      }
    }

    // If fullHead is colored, render all face landmarks with that color
    if (landmarkColors?.fullHead && landmarkColors.fullHead !== 'success') {
      const color = this.getColorForRegion(landmarkColors.fullHead);
      const connectionColor = this.getConnectionColorForRegion(landmarkColors.fullHead);

      // Draw connections
      if (this.options.face.showConnections && this.faceConnections.length > 0) {
        ctx.strokeStyle = connectionColor;
        ctx.lineWidth = adaptiveSizes.width;

        this.faceConnections.forEach(([start, end]) => {
          if (start < landmarks.length && end < landmarks.length) {
            const startLm = landmarks[start];
            const endLm = landmarks[end];
            ctx.beginPath();
            ctx.moveTo(startLm.x * width, startLm.y * height);
            ctx.lineTo(endLm.x * width, endLm.y * height);
            ctx.stroke();
          }
        });
      }

      // Draw all points
      ctx.fillStyle = color;
      landmarks.forEach((landmark) => {
        const x = landmark.x * width;
        const y = landmark.y * height;
        ctx.beginPath();
        ctx.arc(x, y, adaptiveSizes.radius, 0, 2 * Math.PI);
        ctx.fill();
      });
    } else {
      // Render by specific regions
      this.renderFaceLandmarksByRegion(landmarks, width, height, adaptiveSizes, landmarkColors);
    }
  }

  /**
   * Render face landmarks colored by specific regions (brows, lips, nose, eyes, mouth)
   * @param {Array} landmarks - Face landmarks array
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   * @param {Object} adaptiveSizes - Adaptive sizes object
   * @param {Object} landmarkColors - Color configuration
   */
  renderFaceLandmarksByRegion(landmarks, width, height, adaptiveSizes, landmarkColors) {
    const ctx = this.ctx;
    const faceRegions = landmarkColors?.faceRegions || {
      brows: 'success',
      lips: 'success',
      nose: 'success',
      eyes: 'success',
      mouth: 'success',
    };

    // Map region names to landmark indices
    const regionIndices = {
      brows: [...this.faceLandmarksSubset.leftEyebrow, ...this.faceLandmarksSubset.rightEyebrow],
      lips: this.faceLandmarksSubset.lips,
      nose: this.faceLandmarksSubset.nose,
      eyes: [...this.faceLandmarksSubset.leftEye, ...this.faceLandmarksSubset.rightEye],
      mouth: this.faceLandmarksSubset.lips, // Mouth uses same indices as lips
    };

    // Render each region with its specific color
    Object.keys(regionIndices).forEach((regionName) => {
      const regionColor = faceRegions[regionName] || 'success';
      const color = this.getColorForRegion(regionColor);
      const connectionColor = this.getConnectionColorForRegion(regionColor);
      const indices = regionIndices[regionName];
      
      // Draw connections for this region
      if (this.options.face.showConnections) {
        ctx.strokeStyle = connectionColor;
        ctx.lineWidth = adaptiveSizes.width;

        // Find connections that belong to this region
        this.faceConnections.forEach(([start, end]) => {
          if (indices.includes(start) && indices.includes(end) && 
              start < landmarks.length && end < landmarks.length) {
            const startLm = landmarks[start];
            const endLm = landmarks[end];
            ctx.beginPath();
            ctx.moveTo(startLm.x * width, startLm.y * height);
            ctx.lineTo(endLm.x * width, endLm.y * height);
            ctx.stroke();
          }
        });
      }

      // Draw points for this region
      ctx.fillStyle = color;
      indices.forEach((index) => {
        if (index < landmarks.length) {
          const landmark = landmarks[index];
          const x = landmark.x * width;
          const y = landmark.y * height;
          ctx.beginPath();
          ctx.arc(x, y, adaptiveSizes.radius, 0, 2 * Math.PI);
          ctx.fill();
        }
      });
    });

    // Render remaining landmarks (outline, etc.) in default success
    const allRegionIndices = new Set();
    Object.values(regionIndices).forEach((indices) => {
      indices.forEach((idx) => allRegionIndices.add(idx));
    });

    const defaultColor = this.getColorForRegion('success');
    const defaultConnectionColor = this.getConnectionColorForRegion('success');

    // Draw remaining connections
    if (this.options.face.showConnections) {
      ctx.strokeStyle = defaultConnectionColor;
      ctx.lineWidth = adaptiveSizes.width;

      this.faceConnections.forEach(([start, end]) => {
        if (!allRegionIndices.has(start) && !allRegionIndices.has(end) &&
            start < landmarks.length && end < landmarks.length) {
          const startLm = landmarks[start];
          const endLm = landmarks[end];
          ctx.beginPath();
          ctx.moveTo(startLm.x * width, startLm.y * height);
          ctx.lineTo(endLm.x * width, endLm.y * height);
          ctx.stroke();
        }
      });
    }

    // Draw remaining points
    ctx.fillStyle = defaultColor;
    landmarks.forEach((landmark, index) => {
      if (!allRegionIndices.has(index)) {
        const x = landmark.x * width;
        const y = landmark.y * height;
        ctx.beginPath();
        ctx.arc(x, y, adaptiveSizes.radius, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
  }

  /**
   * Render hand landmarks
   * @param {Array} landmarks - Hand landmarks array
   * @param {number} width - Canvas width
   * @param {number} height - Canvas height
   * @param {string} hand - 'left' or 'right'
   * @param {string} colorOverride - Optional color override ('error', 'warning', 'success')
   */
  renderHandLandmarks(landmarks, width, height, hand = 'left', colorOverride = null) {
    const ctx = this.ctx;
    
    // Use color override if provided, otherwise use default colors
    let pointColor, connectionColor;
    if (colorOverride) {
      pointColor = this.getColorForRegion(colorOverride);
      connectionColor = this.getConnectionColorForRegion(colorOverride);
    } else {
      pointColor = hand === 'left' ? this.options.hands.leftColor : this.options.hands.rightColor;
      connectionColor = this.options.hands.connectionColor;
    }

    // Calculate adaptive sizes based on hand bounding box
    const handArea = this.calculateBoundingBoxArea(landmarks, width, height);
    const canvasArea = width * height;
    const adaptiveSizes = this.calculateAdaptiveSize(handArea, canvasArea, 1, 2.5, 0.8, 2);

    // Draw connections (skeleton)
    if (this.options.hands.showConnections) {
      ctx.strokeStyle = connectionColor;
      ctx.lineWidth = adaptiveSizes.width; // Use adaptive width

      this.handConnections.forEach(([start, end]) => {
        if (start < landmarks.length && end < landmarks.length) {
          const startLm = landmarks[start];
          const endLm = landmarks[end];

          // Use direct X coordinate (no mirroring)
          ctx.beginPath();
          ctx.moveTo(startLm.x * width, startLm.y * height);
          ctx.lineTo(endLm.x * width, endLm.y * height);
          ctx.stroke();
        }
      });
    }

    // Draw points
    ctx.fillStyle = pointColor;
    landmarks.forEach((landmark) => {
      // Use direct X coordinate (no mirroring)
      const x = landmark.x * width;
      const y = landmark.y * height;

      ctx.beginPath();
      ctx.arc(x, y, adaptiveSizes.radius, 0, 2 * Math.PI); // Use adaptive radius
      ctx.fill();
    });
  }

  /**
   * Render gaze direction as a line
   */
  renderGazeDirection(gazeData, width, height) {
    if (!this.options.gaze.enabled || !gazeData) return;

    const ctx = this.ctx;

    // Calculate center point (between eyes or face center)
    const centerX = width / 2;
    const centerY = height / 3; // Upper third of frame (eye level)

    // Calculate line direction from gaze_z and gaze_y
    const angle = Math.atan2(gazeData['Gaze Y'] || 0, gazeData['Gaze Z'] || 0);
    const lineLength = this.options.gaze.arrowLength;
    const endX = centerX + Math.cos(angle) * lineLength;
    const endY = centerY + Math.sin(angle) * lineLength;

    // Draw line (no arrow head)
    ctx.strokeStyle = this.options.gaze.arrowColor;
    ctx.lineWidth = this.options.gaze.arrowWidth;
    ctx.lineCap = 'round'; // Rounded line ends

    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(endX, endY);
    ctx.stroke();
  }

  /**
   * Update rendering options
   */
  setOptions(options) {
    Object.assign(this.options, options);
  }
}

export default LandmarksRenderer;
